import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { hostAPI } from '../services/api';
import { ChevronLeft, Mail, CheckCircle2, Loader2, RefreshCw, Eye, EyeOff, XCircle } from 'lucide-react';

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: '1 uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: '1 number', test: (p) => /\d/.test(p) },
  { label: '1 special character', test: (p) => /[!@#$%^&*()_+\-=[\]{};:'"\\|,.<>/?~`]/.test(p) },
];

const HostRegistrationPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Form, 2: Email OTP, 3: Success
  const [loading, setLoading] = useState(false);
  const [hostId, setHostId] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const [emailOtpDigits, setEmailOtpDigits] = useState(['', '', '', '', '', '']);
  const emailOtpRefs = useRef([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const digits = [...emailOtpDigits];
    digits[index] = value.slice(-1);
    setEmailOtpDigits(digits);
    if (value && index < 5) {
      emailOtpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !emailOtpDigits[index] && index > 0) {
      emailOtpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits = ['', '', '', '', '', ''];
    pastedData.split('').forEach((digit, i) => {
      if (i < 6) newDigits[i] = digit;
    });
    setEmailOtpDigits(newDigits);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    const cleanPhone = formData.phone.trim();
    if (!/^\d{10}$/.test(cleanPhone)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return false;
    }
    if (!PASSWORD_RULES.every((r) => r.test(formData.password))) {
      toast.error('Password must meet all the requirements (8+ chars, uppercase, number, special)');
      return false;
    }
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const response = await hostAPI.register(formData);
      
      if (response.success) {
        setHostId(response.host_id);
        toast.success('OTP sent to your email!');
        setStep(2);
        setResendCooldown(60);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOTP = async () => {
    const otp = emailOtpDigits.join('');
    if (otp.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }

    try {
      setLoading(true);
      const response = await hostAPI.verifyOTP({
        host_id: hostId,
        otp_type: 'email',
        otp_code: otp
      });
      
      if (response.success) {
        toast.success('Email verified successfully!');
        setStep(3);
        localStorage.setItem('hostId', hostId);
        localStorage.setItem('hostData', JSON.stringify(response.host));
        localStorage.setItem('hostToken', hostId);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid OTP. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    try {
      setLoading(true);
      const response = await hostAPI.resendOTP({
        host_id: hostId,
        otp_type: 'email'
      });
      
      if (response.success) {
        toast.success('OTP resent to your email!');
        setResendCooldown(60);
      }
    } catch (error) {
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-md">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => step === 1 ? navigate('/') : setStep(step - 1)}
            className="mb-4"
            disabled={step === 3}
            data-testid="host-reg-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {step === 1 ? 'Back to home' : 'Back'}
          </Button>
        </div>

        {/* Step 1: Registration Form */}
        {step === 1 && (
          <Card className="shadow-xl" data-testid="host-reg-form-card">
            <CardHeader className="text-center">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#7CB342] to-[#6da036] flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl">Become a Host</CardTitle>
              <CardDescription>Register to list your homestay on GeoStays</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="mt-2"
                    data-testid="host-name-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="mt-2"
                    data-testid="host-email-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone">Mobile Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="9876543210"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="mt-2"
                    data-testid="host-phone-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter 10-digit mobile number (digits only)</p>
                </div>

                <div>
                  <Label htmlFor="reg-password">Password *</Label>
                  <div className="relative mt-2">
                    <Input
                      id="reg-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="pr-10"
                      data-testid="host-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                      data-testid="host-password-toggle"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {formData.password && (
                    <div className="bg-gray-50 rounded-lg p-2 mt-2 space-y-1">
                      {PASSWORD_RULES.map((rule) => {
                        const ok = rule.test(formData.password);
                        return (
                          <div key={rule.label} className="flex items-center text-xs">
                            {ok ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 mr-2" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5 text-gray-300 mr-2" />
                            )}
                            <span className={ok ? 'text-green-700' : 'text-gray-500'}>{rule.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                  data-testid="host-register-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    'Continue with Email Verification'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Email OTP Verification */}
        {step === 2 && (
          <Card className="shadow-xl" data-testid="host-email-otp-card">
            <CardHeader className="text-center">
              <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl">Verify Your Email</CardTitle>
              <CardDescription>
                Enter the 6-digit code sent to<br />
                <span className="font-semibold text-gray-900">{formData.email}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-center gap-2 sm:gap-3" onPaste={handleOtpPaste}>
                {emailOtpDigits.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => emailOtpRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-[#7CB342] focus:ring-[#7CB342]"
                    data-testid={`otp-input-email-${index}`}
                  />
                ))}
              </div>
              
              <Button
                onClick={verifyEmailOTP}
                disabled={loading || emailOtpDigits.join('').length !== 6}
                className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                data-testid="verify-email-otp-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </Button>

              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">Didn't receive the code?</p>
                <Button
                  variant="ghost"
                  onClick={handleResendOTP}
                  disabled={resendCooldown > 0 || loading}
                  className="text-[#7CB342] hover:text-[#6da036]"
                  data-testid="resend-email-otp-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <Card className="shadow-xl" data-testid="host-reg-success-card">
            <CardHeader className="text-center">
              <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-green-600">Registration Complete!</CardTitle>
              <CardDescription>
                Your email has been verified successfully. You can now list your homestay.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span className="font-semibold">{formData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-semibold">{formData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Phone</span>
                  <span className="font-semibold">{formData.phone}</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/host/dashboard')}
                className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                data-testid="continue-to-dashboard-btn"
              >
                Go to Host Dashboard
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/list-property')}
                className="w-full"
                data-testid="continue-to-listing-btn"
              >
                List a New Property
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Progress Indicator */}
        {step < 3 && (
          <div className="flex justify-center mt-6 gap-2" data-testid="host-reg-progress">
            {[1, 2].map((s) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all ${
                  s === step ? 'w-8 bg-[#7CB342]' : s < step ? 'w-2 bg-[#7CB342]' : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HostRegistrationPage;
